import { useOrgEmployees } from "../../../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack } from '@mui/material';
import DataTable from "../../../../../../../../component/DataTable";
import { useNavigate } from "react-router-dom";
import BtnSecondary from "../../../../../../../../component/SecondaryButton";
import { FilterSelect, StatusChip, PersonCell } from "../../../../../../../../component/listing";
import { getAllAttendanceRequests } from "../../../../../../../../services/attendanceRequest";
import { useNotify } from "../../../../../../../../component/NotificationProvider";
import {
  isAttested, sourceLabel, originTone, raisedByName, isSelfApproved,
} from "../../../../../../../../utils/attendanceRequestOrigin";
import { tokens, displayFont } from "../../../../../../../../theme";

const TYPE_LABEL = {
  REGULARIZATION: "Regularization",
  SHIFT_CHANGE: "Shift Change",
};

const FILTERS = [
  { value: "", label: "All types" },
  { value: "REGULARIZATION", label: "Regularization" },
  { value: "SHIFT_CHANGE", label: "Shift Change" },
];

const STATUS_TONE = { Pending: "warning", Approved: "success", Rejected: "danger" };

// The two lanes are worth separating: pending employee requests are a queue to
// work through, attestations are a record to audit. Mixed together, the queue
// is buried in corrections nobody needs to action.
const ORIGIN_FILTERS = [
  { value: "", label: "All origins" },
  { value: "SELF", label: "Employee requests" },
  { value: "SUPERVISOR_ATTESTED", label: "Supervisor attested" },
  { value: "DEVICE_FAILURE", label: "Device failure" },
  { value: "BULK", label: "Bulk correction" },
];

const RequestsListing = () => {
  const notify = useNotify();
  const navigate = useNavigate();
  const { data: employees } = useOrgEmployees();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [type, setType] = useState("");
  const [source, setSource] = useState("");
  const [data, setData] = useState([]);

  const employeeNameById = useMemo(() => {
    const map = {};
    (employees || []).forEach((e) => { map[e._id] = `${e.fname || ""} ${e.lname || ""}`.trim(); });
    return map;
  }, [employees]);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getAllAttendanceRequests({ type, source });
      if (res?.success === false) throw new Error(res?.message || "Failed to load requests");
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  // `load` is rebuilt every render; the filters are what should retrigger it
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [type, source]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Requests</Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <FilterSelect value={type} options={FILTERS} onChange={(e) => setType(e.target.value)} />
          <FilterSelect value={source} options={ORIGIN_FILTERS} onChange={(e) => setSource(e.target.value)} />
          <BtnSecondary to="/app/attendance-dashboard" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
        </Stack>
      </Stack>

      <DataTable
        rows={data}
        loading={loading}
        error={error}
        onRetry={load}
        errorTitle="Couldn't load requests"
        empty={{ title: "No requests", description: "Regularization and shift-change requests will appear here." }}
        onRowClick={(row) => navigate(`/app/attendance-details/requests/details/${row._id}`)}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          {
            key: "date",
            label: "Date",
            cellSx: { color: tokens.textMuted },
            render: (row) => row?.dayString || (row?.createdAt ? new Date(row.createdAt).toLocaleDateString("en-GB") : "—"),
          },
          {
            key: "employee",
            label: "Employee",
            render: (row) => <PersonCell name={employeeNameById[row?.employeeId] || "Unknown"} />,
          },
          { key: "type", label: "Request Type", render: (row) => TYPE_LABEL[row?.type] || row?.type },
          {
            key: "origin",
            label: "Origin",
            render: (row) => (
              <>
                <StatusChip label={sourceLabel(row)} tone={originTone(row)} />
                {/* naming the author is the point of the column — an
                    assertion with no author is not evidence of anything */}
                {isAttested(row) && raisedByName(row) && (
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
                    by {raisedByName(row)}
                  </Box>
                )}
              </>
            ),
          },
          {
            key: "status",
            label: "Status",
            render: (row) => (
              <>
                <StatusChip label={row?.status} tone={STATUS_TONE[row?.status]} />
                {/* "Approved" on an attested record does not mean a second
                    party checked it: the author approved their own entry,
                    which is correct but must not read as review */}
                {row?.status === "Approved" && isSelfApproved(row) && (
                  <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>
                    not separately reviewed
                  </Box>
                )}
              </>
            ),
          },
        ]}
      />
    </Box>
  );
};

export default RequestsListing;
